import React from 'react';
import { CircularProgress, Box, Typography, Fade, Zoom } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { keyframes } from '@mui/system';

// Custom pulse animation for the loader
const pulse = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

export interface ConflictStatusProps {
  hasConflict: boolean;
  conflictMessage: string | null;
  isChecking?: boolean;
  showNoConflicts?: boolean; // New prop to control when to show "no conflicts"
}

const ConflictStatus: React.FC<ConflictStatusProps> = ({ 
  hasConflict, 
  conflictMessage, 
  isChecking = false, 
  showNoConflicts = true 
}) => {
  if (isChecking) {
    return (
      <Fade in={isChecking}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            mt: 2,
            p: 2,
            backgroundColor: '#f8f9ff',
            border: '1px solid #e3e8ff',
            borderRadius: '12px',
            animation: `${pulse} 2s ease-in-out infinite`,
          }}
        >
          <CircularProgress 
            size={24} 
            thickness={4}
            sx={{ 
              color: '#625afa',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              }
            }} 
          />
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#4c5980', 
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            Checking for scheduling conflicts...
          </Typography>
        </Box>
      </Fade>
    );
  }

  if (hasConflict && conflictMessage) {
    return (
      <Zoom in={hasConflict}>
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <ErrorIcon 
            sx={{ 
              color: '#dc2626', 
              fontSize: '20px',
              mt: 0.25,
              flexShrink: 0,
            }} 
          />
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#7f1d1d',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: 1.4,
            }}
          >
            {conflictMessage}
          </Typography>
        </Box>
      </Zoom>
    );
  }

  // Show "no conflicts" state when not checking and no conflicts found
  if (!isChecking && !hasConflict && showNoConflicts) {
    return (
      <Zoom in={!isChecking && !hasConflict}>
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CheckCircleIcon 
            sx={{ 
              color: '#16a34a', 
              fontSize: '20px',
              flexShrink: 0,
            }} 
          />
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#14532d',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            No scheduling conflicts detected
          </Typography>
        </Box>
      </Zoom>
    );
  }

  return null;
};

export default ConflictStatus; 